import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from "@angular/forms";
import { SecurityApiService } from '@shared/services/security-api.service';
import { CustomFormValidators } from "@shared/validators/custom-form-validators";
import { ToastrService } from 'ngx-toastr';
import { finalize, forkJoin, tap } from 'rxjs';
import { IndividualData } from '@models/individuals/individual-data';
import { ShareholderRelationshipDetails } from '@models/relationships/relationship';
import { CorporateHolderModel } from '@models/shares/security-registry-record';
import {
  MultipleInputNameComponent
} from "@shared/controls/multiple-input-name/multiple-input-name.component";
import { ValidationErrorComponent } from "@shared/controls/validation-error/validation-error.component";

export type IAuthSignatoryFormControl = FormControl<IndividualData[]>;

@Component({
  selector: 'app-auth-signatory',
  standalone: true,
  templateUrl: './auth-signatory.component.html',
  styleUrls: ['./auth-signatory.component.scss', '../../../controls/input/input.component.scss'],
  imports: [
    ReactiveFormsModule,
    MultipleInputNameComponent,
    ValidationErrorComponent,
  ]
})
export class AuthSignatoryComponent implements OnInit {
  private securityService = inject(SecurityApiService);
  private toastr = inject(ToastrService);

  @Input() authSignatoriesControl = AuthSignatoryComponent.defineControl();
  @Input({ required: true }) details: ShareholderRelationshipDetails | null = null;
  @Input({ required: true }) relationshipId!: string;
  @Input({ required: true }) holderName!: string;
  @Input() corporateHolders: CorporateHolderModel[] = [];
  @Input() isPosted = true;
  @Input() controlLike = false;
  @Input() tip = '';
  @Input() customErrors = {
    startsWithDigit: 'The name must start with a letter',
    oneLetterPartName: 'Initials aren\'t allowed'
  };

  @Output() onDataSaved = new EventEmitter<IndividualData[]>();

  isEdit = false;
  isLoading = false;
  allowEdit = true;

  ngOnInit(): void {
    const hasAuthorities = (this.details?.authorisedSignatories?.length ?? 0) > 0;
    this.allowEdit = this.isPosted || !hasAuthorities;
    this.authSignatoriesControl.addValidators([
      Validators.required,
      CustomFormValidators.directorNameValidator,
      CustomFormValidators.directorOneLetterNameValidator
    ]);
  }

  save(): void {
    if (this.authSignatoriesControl.invalid || this.isLoading) {
      this.authSignatoriesControl.markAsTouched();
      return;
    }

    const authSignatories: IndividualData[] = this.authSignatoriesControl.value ?? [];

    this.isLoading = true;

    if (this.corporateHolders.length === 0) {
      // Update from company profile
      this.securityService.updateAuthSignatories(this.relationshipId, authSignatories)
        .pipe(finalize(() => this.isLoading = false))
        .subscribe({
          next: () => {
            this.toastr.success('Authorised signatories updated');
            this.onDataSaved.emit(authSignatories);
            this.isEdit = false;
            if (this.details) {
              this.details.authorisedSignatories = authSignatories;
            }
          },
          error: () => this.toastr.error('Failed to update authorised signatories')
        });
    } else {
      // Update all holders with the same name during document creation
      const updateObservables = this.corporateHolders
        .filter(holder => holder.name === this.holderName && holder.isPosted)
        .map(holder => this.securityService.updateAuthSignatories(holder.relationshipId, authSignatories));

      if (updateObservables.length > 0) {
        this.isLoading = true;
        forkJoin(updateObservables)
          .pipe(tap(() => {
            this.isLoading = false;
            this.isEdit = false;
          }))
          .subscribe({
            next: () => {
              this.updateNotPostedHolders(authSignatories);
              this.toastr.success('Authorised signatories updated');
            },
            error: () => this.toastr.error('Failed to update authorised signatories')
          });
      } else {
        this.updateNotPostedHolders(authSignatories);
        this.isEdit = false;
        this.isLoading = false;
        this.onDataSaved.emit(authSignatories);
      }
    }
  }

  updateNotPostedHolders(authSignatories: IndividualData[]): void {
    this.corporateHolders
      .filter((holder) => holder.name === this.holderName)
      .forEach(holder => {
        holder.details.authorisedSignatories = authSignatories;
      });
  }

  startEditing(): void {
    if (this.isLoading) return;
    this.authSignatoriesControl.setValue([...(this.details?.authorisedSignatories ?? [])]);
    this.isEdit = true;
  }

  cancelEditing(): void {
    if (this.isLoading) return;
    this.authSignatoriesControl.setValue([...(this.details?.authorisedSignatories ?? [])]);
    this.isEdit = false;
  }

  get authSignatories(): string {
    return this.details?.authorisedSignatories?.length
      ? this.details?.authorisedSignatories.map(signatory => signatory.fullNameLabel).join('; ')
      : '-';
  }

  static defineControl(individuals: IndividualData[] = []): IAuthSignatoryFormControl {
    return new FormControl<IndividualData[]>(individuals, { nonNullable: true });
  }
}
